/**
 * PromoterDashboardFundraiserDetails.tsx - Fundraiser Information Component
 *
 * This component displays detailed fundraiser information within the promoter
 * dashboard context with promoter-specific metrics and progress tracking.
 *
 * Key Features:
 * - Fundraiser overview with key details and status
 * - Promoter-specific progress and contribution metrics
 * - Goal tracking with individual and overall progress
 * - Timeline information and important dates
 * - Status indicators and expiration warnings
 * - Quick action buttons for promoter activities
 *
 * Fundraiser Information:
 * - Title, description, and organization details
 * - Overall fundraising goal and current progress
 * - Start and end dates with time remaining
 * - Category and cause information
 * - Status (active, completed, paused, etc.)
 *
 * Promoter Context:
 * - Individual promoter goal and progress
 * - Contribution to overall fundraiser success
 * - Ranking among other promoters
 * - Personal achievement milestones
 * - Reward eligibility and status
 *
 * Quick Actions:
 * - Share promoter-specific fundraiser link
 * - Make a personal donation
 * - View full fundraiser page
 * - Access promotional materials
 *
 * Props:
 * - fundraiserId: string - ID of fundraiser to display
 * - promoterId: string - ID of promoter for context
 *
 * Dependencies: MobX store, fundraiser utilities, badge utilities
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Search, ChevronDown, Check } from "lucide-react";
import { ProgressSection } from "../ProgressSection";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { Fundraiser } from "../../models/FundraisersGridModels";
import { FundraiserService } from "../../services";
import { firstValueFrom } from "rxjs";
import { AuthService, withRouter } from "@mindmemobile/mm-shared-global";
import { CreateFundraiserModel, RelatedFundRaisersModel } from "../../models";
import { FundRaiserStatus } from "../../enums";
import { NavigateFunction } from "react-router-dom";

const FundraiserDetailsBlock = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  margin: 32px 0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const FundraiserTitleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const FundraiserTitle = styled.h2<{ isClickable: boolean }>`
  font-size: 36px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  text-align: center;
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: ${(props) => (props.isClickable ? "#3b82f6" : "#1e293b")};
  }
`;

const DropdownIcon = styled(ChevronDown) <{ isOpen: boolean; visible: boolean }>`
  width: 20px;
  height: 20px;
  color: #64748b;
  transition: all 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  cursor: pointer;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};

  &:hover {
    color: #3b82f6;
  }
`;

const FundraiserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow: hidden;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  margin-top: 8px;
  min-width: 560px;
`;

const DropdownHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
`;

const DropdownSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #f8fafc;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const DropdownSearchIcon = styled(Search)`
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #64748b;
`;

const DropdownList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.2fr 40px;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownFundraiserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DropdownFundraiserThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const DropdownFundraiserDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownFundraiserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownFundraiserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownStatusBadge = styled.div<{
  status: keyof typeof FundRaiserStatus;
}>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;

  ${(props) => {
    switch (props.status) {
      case FundRaiserStatus.Active:
        return "background: #dcfce7; color: #166534;";
      case FundRaiserStatus.Completed:
        return "background: #dbeafe; color: #1e40af;";
      case FundRaiserStatus.Inactive:
        return "background: #fef3c7; color: #92400e;";
      case FundRaiserStatus.Draft:
        return "background: #f3f4f6; color: #374151;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const DropdownProgressColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DropdownProgressAmounts = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const DropdownProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownProgressBar = styled.div`
  width: 80px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
`;

const DropdownProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 2px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  animation: progressLoadPromoterDash 0.8s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes progressLoadPromoterDash {
    from {
      width: 0%;
    }
    to {
      width: ${(props) => Math.min(props.percentage, 100)}%;
    }
  }
`;

const DropdownProgressPercentage = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #1e293b;
  min-width: 35px;
`;

const SelectedCheckIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #10b981;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
`;

const FundraiserSubtitle = styled.p`
  font-size: 20px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.4;
  text-align: center;
`;

interface PromoterDashboardFundraiserDetailsProps {
  promoterId: string;
  fundraiserId: string;
  fundraiser: CreateFundraiserModel;
  navigate: NavigateFunction;
}

const PromoterDashboardFundraiserDetails: React.FC<PromoterDashboardFundraiserDetailsProps> =
  observer(({ promoterId, fundraiserId, fundraiser, navigate }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Initialize builder data for the current fundraiser
    useEffect(() => {
      // const currentFundraiser = fundraiserStore.fundraisers.find(
      //   (f) => f.id === fundraiserId
      // );
      // if (currentFundraiser) {
      //   // Only initialize if builder data is for a different fundraiser or empty
      //   if (
      //     !fundraiserStore.builderData.id ||
      //     fundraiserStore.builderData.id !== fundraiserId
      //   ) {
      //     fundraiserStore.initializeBuilder(currentFundraiser);
      //   }
      // }

      fetchFundraiserData();
    }, [fundraiserId]);

    const fetchFundraiserData = async () => {
      try {
        // setShowLoader(true);
        // const response = await firstValueFrom(
        //   FundraiserService.getFundraiserData(
        //     AuthService.authData.accountId,
        //     fundraiserId,
        //     promoterId
        //   )
        // );

        // const mappedBuilderData = CreateFundraiserModel.deserializeAPIModel(response.result);
        // //const data = Fundraiser.deserializeAPIModel(response.result);
        fundraiserStore.initializeBuilder(fundraiser);
      } catch (error) {
        console.error("Failed to load fundraisers", error);
      } finally {
        //setShowLoader(false);
      }
    };

    // Handle clicking outside dropdown to close it
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          if (showDropdown) {
            setShowDropdown(false);
            setSearchTerm("");
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showDropdown]);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (showDropdown && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    }, [showDropdown]);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    // Get fundraisers that the promoter has access to
    const getPromoterFundraisers = () => {
      // // Get current promoter's contactId
      // const currentPromoter = fundraiserStore.getPromoterById(promoterId);
      // if (!currentPromoter?.contactId) {
      //   return [];
      // }

      // Use the new store method to get fundraisers by contactId, excluding current fundraiser
      return fundraiser
        .relatedFundraisers?.filter((f) => f.id !== fundraiserId) || [];
    };

    // Filter fundraisers based on search term
    const getFilteredFundraisers = () => {
      const availableFundraisers = fundraiser.relatedFundraisers || [];
      if (!searchTerm) return availableFundraisers;

      const searchLower = searchTerm.toLowerCase();
      return availableFundraisers.filter(
        (f) => f.name.toLowerCase().includes(searchLower)
        //||
        // f.title.toLowerCase().includes(searchLower) ||
        // (f.subtitle && f.subtitle.toLowerCase().includes(searchLower)) ||
        // (f.category && f.category.toLowerCase().includes(searchLower))
      );
    };

    const handleFundraiserSelect = (
      selectedFundraiser: RelatedFundRaisersModel
    ) => {
      // // Find the correct promoter ID for this contact in the selected fundraiser
      // const currentPromoter = fundraiserStore.getPromoterById(promoterId);
      // if (!currentPromoter?.contactId) return;

      // const targetPromoterForFundraiser = fundraiserStore.promoters.find(
      //   (p) =>
      //     p.contactId === currentPromoter.contactId &&
      //     p.fundraiserId === selectedFundraiser.id
      // );

      // if (targetPromoterForFundraiser) {
      //   // Navigate using the correct promoter ID for the target fundraiser
      //   fundraiserStore.setSelectedPromoter(targetPromoterForFundraiser.id);
      //   fundraiserStore.setSelectedFundraiser(selectedFundraiser.id);
      //   navigate(`/fundraiser/promoter-dashboard`);
      // }
      fundraiserStore.setSelectedFundraiser(selectedFundraiser.id);
      navigate(`/fundraiser/promoter-dashboard/${selectedFundraiser.id}/?PID=${promoterId}`);
      setShowDropdown(false);
      setSearchTerm("");
    };

    const handleToggleDropdown = () => {
      setShowDropdown(!showDropdown);
      setSearchTerm("");
    };

    const getFundraiserTitle = () => {
      return fundraiser.title; //|| fundraiserStore.selectedFundraiser?.title || ""
    };

    const getFundraiserSubtitle = () => {
      return fundraiser.subtitle || fundraiser?.description || "";
    };

    const availableFundraisers = getPromoterFundraisers();
    const hasMultipleFundraisers = availableFundraisers.length > 0;

    return (
      <FundraiserDetailsBlock>
        <FundraiserTitleContainer ref={dropdownRef}>
          <FundraiserTitle
            isClickable={hasMultipleFundraisers}
            onClick={hasMultipleFundraisers ? handleToggleDropdown : undefined}
          >
            {getFundraiserTitle()}
          </FundraiserTitle>
          <DropdownIcon
            isOpen={showDropdown}
            visible={hasMultipleFundraisers}
            onClick={hasMultipleFundraisers ? handleToggleDropdown : undefined}
          />

          {hasMultipleFundraisers && (
            <FundraiserDropdown isOpen={showDropdown}>
              <DropdownHeader>
                <DropdownSearchIcon />
                <DropdownSearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search fundraisers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </DropdownHeader>

              <DropdownList>
                {getFilteredFundraisers().map((f) => {
                  const progress =
                    f.goalAmount > 0
                      ? (f.totalRaisedAmount / f.goalAmount) * 100
                      : 0;
                  const barProgress = Math.min(progress, 100);
                  const isSelected = f.id === fundraiserId;
                  return (
                    <DropdownItem
                      key={f.id}
                      onClick={() => handleFundraiserSelect(f)}
                    >
                      <DropdownFundraiserInfo>
                        {f.image && (
                          <DropdownFundraiserThumb
                            src={f.image?.fullURL || ""}
                          />
                        )}

                        <DropdownFundraiserDetails>
                          <DropdownFundraiserName>
                            {f.name}
                          </DropdownFundraiserName>
                          <DropdownFundraiserMeta>
                            <DropdownStatusBadge status={f.status}>
                              {f.status}
                            </DropdownStatusBadge>
                          </DropdownFundraiserMeta>
                        </DropdownFundraiserDetails>
                      </DropdownFundraiserInfo>

                      <DropdownProgressColumn>
                        <DropdownProgressAmounts>
                          {formatCurrency(f.totalRaisedAmount)} /{" "}
                          {formatCurrency(f.goalAmount)}
                        </DropdownProgressAmounts>
                        <DropdownProgressBarContainer>
                          <DropdownProgressBar>
                            <DropdownProgressFill percentage={barProgress} />
                          </DropdownProgressBar>
                          <DropdownProgressPercentage>
                            {progress.toFixed(1)}%
                          </DropdownProgressPercentage>
                        </DropdownProgressBarContainer>
                      </DropdownProgressColumn>

                      {isSelected && <SelectedCheckIcon>✓</SelectedCheckIcon>}
                    </DropdownItem>
                  );
                })}

                {getFilteredFundraisers().length === 0 && (
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "14px",
                    }}
                  >
                    {searchTerm
                      ? `No fundraisers found matching "${searchTerm}"`
                      : "No fundraisers available"}
                  </div>
                )}
              </DropdownList>
            </FundraiserDropdown>
          )}
        </FundraiserTitleContainer>

        <FundraiserSubtitle>{getFundraiserSubtitle()}</FundraiserSubtitle>
        <ProgressSection
          showFundraiser={true}
          showPromoter={true}
          promoterId={promoterId}
          hideExpirationBadge={true}
          promoterProgress={fundraiser.promoterProgress}
          fundraiser={fundraiser}
        />
      </FundraiserDetailsBlock>
    );
  });
export default withRouter(PromoterDashboardFundraiserDetails);
